import { sortAccountGroups, groupAccounts } from "../../../../AccountUtils";

import type Account from "../../../../Account";
import type { selectAccountSorting } from "./selectors";

const shortGroupTitle = (title: string) =>
  title === "External accounts" ? "External" : title;

type AccountSortingList = ReturnType<typeof selectAccountSorting>;

const flattenGroups = (
  groups: ReturnType<typeof groupAccounts<Account>>,
  sorting: AccountSortingList,
) => {
  sortAccountGroups(groups, sorting); // updates inplace
  const groupsArray = Array.from(groups);

  return groupsArray.map(([title, accounts]) => {
    return {
      title: shortGroupTitle(title),
      accountIds: accounts.map((a) => a.id as API.AccountId),
      accounts,
    };
  });
};

export const groupAccountsByOrganization = (
  accounts: Account[],
  mainOrganizationName?: string,
): Map<string, Account[]> => {
  const groupedAccounts: { [name: string]: Account[] } = {};

  accounts.forEach((account) => {
    const orgName =
      account.organization?.name_dba ||
      account.organization?.name_legal ||
      mainOrganizationName;

    if (!orgName) return;

    if (!groupedAccounts[orgName]) {
      groupedAccounts[orgName] = [];
    }
    groupedAccounts[orgName].push(account);
  });

  return new Map(
    Object.entries(groupedAccounts).sort(([nameA], [nameB]) =>
      nameA.localeCompare(nameB),
    ),
  );
};

export const createGroups = (
  accounts: Account[],
  sorting: AccountSortingList,
  mainOrganizationName?: string,
) => {
  const orgGrouped = groupAccountsByOrganization(
    accounts,
    mainOrganizationName,
  );
  if (orgGrouped.size > 1) {
    return Array.from(orgGrouped).map(([title, orgAccounts]) => ({
      title,
      accountIds: orgAccounts.map((a) => a.id as API.AccountId),
      accounts: orgAccounts,
    }));
  }

  return flattenGroups(groupAccounts(accounts), sorting);
};

export const isLoan = (account: Account) => {
  return ["mortgage", "lease", "loan", "line_of_credit"].includes(
    account.product_type,
  );
};
